import React, { useState, useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';

const SocialProof = () => {
  const [notifications, setNotifications] = useState([]);
  const [nextId, setNextId] = useState(1);

  // Mix of Indian and International names with locations
  const enrollmentData = [
    { name: 'Rahul Sharma', location: 'Mumbai, India', course: 'Ethical Hacking Course' },
    { name: 'Priya Patel', location: 'Gujarat, India', course: 'Bug Bounty Hunter' },
    { name: 'John Smith', location: 'New York, USA', course: 'SOC Analyst Training' },
    { name: 'Amit Kumar', location: 'Delhi, India', course: 'Digital Forensics' },
    { name: 'Sarah Johnson', location: 'London, UK', course: 'VAPT Master Course' },
    { name: 'Vikram Singh', location: 'Bangalore, India', course: 'Ethical Hacking Course' },
    { name: 'Mohammed Ali', location: 'Dubai, UAE', course: 'SOC Analyst Training' },
    { name: 'Anjali Reddy', location: 'Hyderabad, India', course: 'Bug Bounty Hunter' },
    { name: 'David Chen', location: 'Singapore', course: 'VAPT Master Course' },
    { name: 'Sneha Gupta', location: 'Pune, India', course: 'Digital Forensics' },
    { name: 'Robert Brown', location: 'Sydney, Australia', course: 'Linux for Cybersecurity' },
    { name: 'Kavya Menon', location: 'Kerala, India', course: 'Python for Hackers' },
    { name: 'Michael Lee', location: 'Toronto, Canada', course: 'Web App Penetration Testing' },
    { name: 'Deepak Joshi', location: 'Jaipur, India', course: 'Ethical Hacking Course' },
    { name: 'Emily Davis', location: 'California, USA', course: 'Cybersecurity Advance Training' },
    { name: 'Arjun Nair', location: 'Chennai, India', course: 'SOC Analyst Training' },
    { name: 'Lisa Wong', location: 'Hong Kong', course: 'Bug Bounty Hunter' },
    { name: 'Karan Mehta', location: 'Ahmedabad, India', course: 'VAPT Master Course' },
    { name: 'James Wilson', location: 'Texas, USA', course: 'Digital Forensics' },
    { name: 'Neha Iyer', location: 'Mumbai, India', course: 'Ethical Hacking Course' }
  ];

  // Time ago options
  const timeAgoOptions = [
    'just now',
    '1 minute ago',
    '2 minutes ago',
    '3 minutes ago',
    '5 minutes ago',
    '10 minutes ago'
  ];

  const playNotificationSound = () => {
    try {
      // Create a very subtle notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Subtle frequency and very low volume
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.03; // Very low volume (3%)

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1); // Short duration
    } catch (error) {
      // Silently fail if audio context is not supported
      console.log('Audio notification not available');
    }
  };

  const showNotification = () => {
    const randomEnrollment = enrollmentData[Math.floor(Math.random() * enrollmentData.length)];
    const randomTimeAgo = timeAgoOptions[Math.floor(Math.random() * timeAgoOptions.length)];
    
    const newNotification = {
      id: nextId,
      ...randomEnrollment,
      timeAgo: randomTimeAgo
    };

    setNotifications(prev => [...prev, newNotification]);
    setNextId(prev => prev + 1);
    playNotificationSound();

    // Auto remove after 5 seconds (shorter duration)
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
    }, 5000);
  };

  useEffect(() => {
    let timeoutId;
    let isComponentMounted = true;

    const scheduleNextNotification = () => {
      if (!isComponentMounted) return;
      
      // Random delay between 30-45 seconds for realistic feel
      const delay = Math.random() * 15000 + 30000;
      
      timeoutId = setTimeout(() => {
        if (isComponentMounted) {
          showNotification();
          // Schedule next one after this notification disappears (5s) + random gap
          setTimeout(() => {
            scheduleNextNotification();
          }, 5000); // Wait for current notification to disappear
        }
      }, delay);
    };

    // Show first notification after 10 seconds
    const initialTimeout = setTimeout(() => {
      if (isComponentMounted) {
        showNotification();
        // Start the cycle after first notification disappears
        setTimeout(() => {
          scheduleNextNotification();
        }, 5000);
      }
    }, 10000);

    return () => {
      isComponentMounted = false;
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-3 pointer-events-none">
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className="pointer-events-auto transform transition-all duration-500 ease-out"
          style={{
            animation: 'slideInLeft 0.5s ease-out, fadeIn 0.5s ease-out',
            animationDelay: '0s'
          }}
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 border-2 border-red-500/30 rounded-xl shadow-2xl shadow-red-500/20 p-4 min-w-[320px] max-w-[380px] backdrop-blur-lg relative overflow-hidden group hover:border-red-500/50 transition-all duration-300">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-orange-500/5 to-red-500/5 animate-pulse opacity-50"></div>
            
            {/* Content */}
            <div className="relative flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Text Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{notification.name}</p>
                    <p className="text-gray-400 text-xs truncate">{notification.location}</p>
                  </div>
                  <button
                    onClick={() => removeNotification(notification.id)}
                    className="flex-shrink-0 text-gray-500 hover:text-white transition-colors p-1 hover:bg-gray-800 rounded"
                    aria-label="Close notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-gray-300">
                    Just enrolled in <span className="text-red-400 font-semibold">{notification.course}</span>
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    {notification.timeAgo}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-500"></div>
          </div>
        </div>
      ))}
      
      <style jsx>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default SocialProof;